import { useHistory } from "react-router-dom";
import { isB2bOrganisation } from "../../../../api/src/shared/domain/organisation";
import {
  IconCalendarEvent,
  IconPlus,
  IconSchool,
  IconStar,
} from "@tabler/icons-react";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { RoomSectionTab } from "../../components/ui/RoomSectionTab";
import { ScrollFadeRow } from "../../components/ui/ScrollFadeRow";
import { RoomHeader } from "../../components/room/RoomHeader";
import { Tabs } from "../../components/ui/Tabs";
import ArticlePublicationCard from "../../components/cards/ArticlePublicationCard";
import {
  hasReadArticle,
  isRecentArticle,
} from "../../domains/article/utils/articleReads";
import InfographicCard from "../../components/cards/InfographicCard";

import PlaylistCard from "../../components/cards/PlaylistCard";
import PodcastCard from "../../components/cards/PodcastCard";
import VideoCard from "../../components/cards/VideoCard";
import { ContentPublicationCard } from "../../components/ui/ContentPublicationCard";
import { thumbnailUrl } from "../../../../api/src/shared/domain/video-url";
import { usePlayAudioContent } from "../../domains/room/hooks/usePlayAudioContent";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import RoomEventDisplay from "../../components/room/RoomEventDisplay";
import { RoomCongressResources } from "../../components/room/RoomCongressResources";
import { useNow } from "../../common/hooks/useNow";
import { RoomListWrapper } from "../../components/room/layout";
import { getRoomColors } from "../../domains/room/utils/getRoomColors";
import { useRoomInfoHeader } from "../../domains/room/hooks/useRoomInfoHeader";
import { useJoinRoom } from "../../domains/room/hooks/useJoinRoom";
import { useCanShowJoin } from "../../domains/room/hooks/useCanShowJoin";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";

import { useShareRoom } from "../../domains/room/hooks/useShareRoom";
import { DebugLogger } from "../../domains/app/utils/debugLogger";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { gaEventSelectItemPlaylist } from "../../tools/analytics/domains/playlist.analytics";
import {
  useGetRoomResourceItemsQuery,
  toResourceListItems,
  type RoomResourceListEntry,
} from "../../domains/room/hooks/useGetRoomResourceItemsQuery";
import { resourceItemKey } from "../../../../api/src/shared/domain/room-resources";
import type { ResolvedRoomSection } from "../../domains/room/hooks/useGetRoomSectionsQuery";
import { EventDisplayDestination } from "../../../../api/src/shared/domain/room";
import { resourceChips } from "../../domains/room/utils/resourceChips";
import type { PrivateRoom } from "../../domains/room/privateRoom";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { gaEventViewItemListMixedContent } from "../../tools/analytics/domains/content.analytics";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";
import { useLayoutEffect, useRef, useState } from "react";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import RoomExplainerBottomSheet from "../../components/room/RoomExplainerBottomSheet";
import { roomDiscoveryChannelSchema } from "../../tools/analytics/domains/invite.analytics";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import Loader from "../../components/Loader";
import { z } from "zod";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { roomAudioSource } from "../../domains/room/utils/roomResourceItem";

function formatAudioDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

function formatContentDate(
  value: Date | string | null,
  locale: string
): string {
  if (value === null) return "";
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const debug = new DebugLogger("Room Resources Page");
debug.mute();
const ANALYTICS_LIST_NAME = AnalyticsItemList.ROOM_RESOURCES;

// The congress grace window closes on a day boundary, so a minute is already
// far finer than the thing being watched. It matches the countdown card's own
// tick, which is what makes one shared `now` enough for the whole tab.
const CONGRESS_TICK_MS = 60_000;

/**
 * The filter the tab applies for a `?section=`: the id as given, or "" ("All")
 * once the entries are loaded and none of their chips has that id. The server
 * serves no section with nothing this reader can open, no deleted one, and no
 * congress space to a reader its routes do not answer (signed out, since
 * #2065), so a link to any of them lands on "All", chip included, as on
 * desktop. `servedFilterIds` are the chip ids: a section's id, and
 * `congress:<id>` for a congress space (`resourceChips`). Exported for its
 * test.
 */
export function sectionFilterId(args: {
  sectionIdFromUrl: string;
  servedFilterIds: readonly string[] | null;
}): string {
  if (args.servedFilterIds === null) return args.sectionIdFromUrl;
  return args.servedFilterIds.includes(args.sectionIdFromUrl)
    ? args.sectionIdFromUrl
    : "";
}

/**
 * What the list shows: on "All", the room's merged list as the server ordered
 * it, each congress space a block at its place and without the documents those
 * blocks show; on a section chip, that section's items in CX's order. Exported
 * for its test.
 */
export function displayedResourceItems(args: {
  allItems: readonly RoomResourceListEntry[];
  selectedSection: ResolvedRoomSection | null;
}): readonly RoomResourceListEntry[] {
  return args.selectedSection === null
    ? args.allItems
    : toResourceListItems(args.selectedSection.orderedItems);
}

/**
 * The congress blocks that open the list sit above the room's event, where the
 * blocks always sat; the others are blocks inside the list, at their places.
 * Exported for its test.
 */
export function placedBlocks<T extends { kind: string }>(
  items: readonly T[]
): { leadingBlocks: readonly T[]; listedItems: readonly T[] } {
  const firstCard = items.findIndex((item) => item.kind !== "congress");
  const end = firstCard === -1 ? items.length : firstCard;
  return { leadingBlocks: items.slice(0, end), listedItems: items.slice(end) };
}

/**
 * Where selecting a section chip navigates, and what it stamps on the way.
 *
 * Module scope and exported so the `"chip"` literal can be asserted without
 * mounting this page. That literal is an ANALYTICS CONTRACT, not a detail:
 * `RoomCongressResources` reports it verbatim as `entry_point` on
 * `congress_space_viewed`, and it honours whatever it is handed — so it cannot
 * tell a wrong-but-valid value from the right one. All three doors into a
 * congress space are therefore pinned at their source (the two countdown heroes
 * have their own tests); this is the third.
 *
 * `state: undefined` on the non-congress branch is react-router's own "no
 * state", and it matters: without the distinction a chip tap and an external
 * deep link land on the SAME url and become indistinguishable. A plain section
 * filter is not a congress entry and must carry nothing.
 */
export function buildSectionSelectTarget(params: {
  sectionId: string;
  pathname: string;
  search: string;
}): {
  pathname: string;
  search: string;
  state: { congressEntryPoint: "chip" } | undefined;
} {
  const next = new URLSearchParams(params.search);
  if (params.sectionId === "") {
    next.delete("section");
  } else {
    next.set("section", params.sectionId);
  }
  const q = next.toString();
  return {
    pathname: params.pathname,
    search: q ? `?${q}` : "",
    state: params.sectionId.startsWith("congress:")
      ? { congressEntryPoint: "chip" }
      : undefined,
  };
}

export default function RoomResourcesPage({
  roomId,
  room,
}: {
  roomId: string;
  room: PrivateRoom;
}) {
  const history = useHistory();
  const { publicPlaylistLink } = usePageLinks(roomId);
  const { roomDiscoveryChannel, roomsSearch } = useLocationState(
    z.object({
      roomDiscoveryChannel: roomDiscoveryChannelSchema.default("public app"),
      roomsSearch: z.string().default(""),
    })
  );
  const { t } = useTranslation();
  // TWO bindings in scope here — do not reach for whichever is nearer.
  //   contentLang  -> api `language` query filters (content corpus)
  //   activeLang   -> UI chrome
  const { activeLang, contentLang } = useActiveLang();
  const sectionIdFromUrl =
    new URLSearchParams(history.location.search).get("section") ?? "";
  const resourcesQuery = useGetRoomResourceItemsQuery(roomId, "exclude");
  const sections =
    resourcesQuery.state === "success" ? resourcesQuery.sections : [];
  const selectedSection =
    sections.find((s) => s.id === sectionIdFromUrl) ?? null;
  const activeSectionId = sectionFilterId({
    sectionIdFromUrl,
    servedFilterIds:
      resourcesQuery.state === "success"
        ? resourcesQuery.entries.map((entry) =>
            entry.kind === "section" ? entry.id : `congress:${entry.id}`
          )
        : null,
  });
  function handleSectionSelect(sectionId: string) {
    history.replace(
      buildSectionSelectTarget({
        sectionId,
        pathname: history.location.pathname,
        search: history.location.search,
      })
    );
  }
  // The room's Resources order: its sections AND its congress spaces, as the
  // server ordered them (domain/room-order.ts). Rendered as is, never sorted.
  const entries =
    resourcesQuery.state === "success" ? resourcesQuery.entries : [];
  // ONE ticker for the tab. `RoomCongressResources` below takes it as a prop
  // rather than running its own: the chip and the block it filters decide the
  // same congress/section presentation, and two tickers could disagree for a
  // minute at the window boundary.
  const now = useNow(CONGRESS_TICK_MS);
  const activeCongressSpaceId = activeSectionId.startsWith("congress:")
    ? activeSectionId.slice("congress:".length)
    : null;
  const isCongressFilter = activeCongressSpaceId !== null;
  // Congress content shows when nothing or a congress chip is selected; a
  // room-section filter hides it.
  const congressVisible = activeSectionId === "" || isCongressFilter;
  // On "All" both subtrees render, and a document may be BOTH attached to a
  // congress space and listed in a room section. The congress block wins (it
  // renders above): the server leaves its documents out of "All" ("exclude").
  // Under a section filter the congress subtree is hidden, and the section's
  // copy is all there is.
  const sectionButtons = [
    { id: "", label: t("All"), icon: "star" as const, newContent: false },
    ...resourceChips(entries, { now, contentLang, activeLang }),
  ];
  const { currentUser } = useCurrentUser();
  const playAudioContent = usePlayAudioContent();
  const currentRoom = room;
  const { roomTextColor } = getRoomColors(room.primaryColor);

  const { triggerShare, renderShareSheet } = useShareRoom(roomId, room);
  // A section chip narrows to that section's curation; "All" shows the room's
  // whole Resources set — curated items and raw member media alike.
  const allItems =
    resourcesQuery.state === "success" ? resourcesQuery.items : [];
  const displayedItems = displayedResourceItems({
    allItems,
    selectedSection,
  });
  const { leadingBlocks, listedItems } = placedBlocks(displayedItems);
  const cardCount = displayedItems.filter(
    (item) => item.kind !== "congress"
  ).length;
  const hadCachedSections = useRef(resourcesQuery.state === "success");
  const scrollRef = useRef<HTMLDivElement>(null);
  const { savePageScrollY, restorePageScrollY } = usePageScroll(scrollRef);
  const canShowJoin = useCanShowJoin(roomId, room);
  const [isExplainerOpen, setExplainerOpen] = useState(false);
  const { joinRoom, isJoining } = useJoinRoom(
    roomId,
    roomDiscoveryChannel,
    "greet"
  );
  const joinButton = canShowJoin ? (
    <Button
      variant="primary"
      className="w-full"
      disabled={isJoining}
      leftIcon={isJoining ? null : <IconPlus />}
      onClick={joinRoom}
    >
      {isJoining ? <Loader /> : t("Join")}
    </Button>
  ) : null;
  const {
    handleScroll: handleRoomInfoScroll,
    hasScrolledPastInfo,
    RoomInfoSection,
  } = useRoomInfoHeader(roomId, {
    footer: joinButton,
    onInfoClick: () => {
      setExplainerOpen(true);
    },
  });

  // Scroll to top on forward navigation (tab switch), restore on back
  useLayoutEffect(() => {
    if (history.action !== "POP") {
      scrollRef.current?.scrollTo({ top: 0, behavior: "instant" });
    }
  }, []);

  useEffectOnce(() => {
    if (history.action === "POP" && displayedItems.length > 0) {
      restorePageScrollY();
    }
  }, [resourcesQuery.state]);

  const displayedItemsKey = displayedItems
    .map((item) => resourceItemKey(item))
    .join(",");
  useEffectOnce(() => {
    const trackedContent = displayedItems.flatMap((item) =>
      item.kind === "content" ? [item.content] : []
    );
    const trackedPodcasts = displayedItems.flatMap((item) =>
      item.kind === "podcast" ? [item.podcast] : []
    );
    if (trackedContent.length > 0 || trackedPodcasts.length > 0) {
      gaEventViewItemListMixedContent({
        content: trackedContent,
        podcasts: trackedPodcasts,
        listName: ANALYTICS_LIST_NAME,
      });
    }
  }, [displayedItemsKey]);

  function handleScrollEnd() {
    if (resourcesQuery.state === "success" && resourcesQuery.canShowMore) {
      resourcesQuery.showMore();
    }
  }

  return (
    <>
      <ScrollableLayout
        scrollRef={scrollRef}
        headerClassName="bg-white/90 backdrop-blur-md"
        onScroll={() => {
          handleRoomInfoScroll();
          savePageScrollY();
        }}
        header={
          <RoomHeader
            roomId={roomId}
            backTo={`/rooms${roomsSearch}`}
            desktopBackTo={`/room/discovery/${roomId}`}
            backLabel={null}
            showJoinButton={hasScrolledPastInfo && canShowJoin}
            currentUser={currentUser}
            onJoinClick={joinRoom}
            onShareClick={() => {
              void triggerShare();
            }}
          />
        }
        footer={null}
        style={{ paddingTop: 0, overscrollBehavior: "none" }}
        onScrollEnd={handleScrollEnd}
      >
        {RoomInfoSection}

        <Tabs
          className="sticky top-[calc(48px+var(--safe-area-top))] z-30"
          tabs={[
            {
              value: "discovery",
              label: t("Discovery"),
              badge: null,
              icon: null,
            },
            {
              value: "resources",
              label: t("Resources"),
              badge: null,
              icon: null,
              activeStyle: {
                color: currentRoom.primaryColor ?? undefined,
                backgroundColor:
                  currentRoom.primaryColor !== null
                    ? `${currentRoom.primaryColor}0F`
                    : undefined,
              },
              inactiveStyle: {
                color: currentRoom.primaryColor ?? undefined,
                backgroundColor:
                  currentRoom.primaryColor !== null
                    ? `${currentRoom.primaryColor}08`
                    : undefined,
              },
              indicatorStyle:
                currentRoom.primaryColor !== null
                  ? { backgroundColor: currentRoom.primaryColor }
                  : undefined,
            },
          ]}
          activeTab="resources"
          onTabChange={(tab) => {
            if (tab === "discovery") {
              history.push(`/room/discovery/${roomId}`, {
                roomDiscoveryChannel,
                roomsSearch,
              });
            }
          }}
        />

        <div className="bg-white pb-12">
          <div className="px-5 py-4 flex flex-col gap-4">
            {/* The partner selects the Resources: the band tells the reader
                that this tab is not Juisci's own editorial selection
                (Discovery). The desktop tab shows the same band. */}
            {isB2bOrganisation(room.organisationModel.id) ? (
              <p className="m-0 rounded-lg bg-ds-slate-100 px-3.5 py-2.5 text-footnote text-ds-slate-600">
                {t("Resources selected by {{partner}}", {
                  partner: room.organisationModel.name,
                })}
              </p>
            ) : null}
            {resourcesQuery.state === "pending" && (
              <div className="-mx-5 overflow-x-auto">
                <div className="flex items-start gap-2 px-5 py-1">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div
                      key={index}
                      className="flex w-16 shrink-0 flex-col items-center gap-2"
                    >
                      <div className="size-10 rounded-xl bg-ds-slate-100 animate-pulse" />
                      <div className="h-3 w-12 rounded bg-ds-slate-100 animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {resourcesQuery.state === "success" && entries.length > 0 && (
              <ScrollFadeRow className="-mx-5">
                <div className="flex items-start gap-2 px-5 py-1">
                  {sectionButtons.map((section) => {
                    const Icon =
                      section.icon === "star"
                        ? IconStar
                        : section.icon === "congress"
                          ? IconCalendarEvent
                          : IconSchool;
                    return (
                      <RoomSectionTab
                        key={section.id}
                        label={section.label}
                        icon={<Icon className="size-5 stroke-[1.5px]" />}
                        active={section.id === activeSectionId}
                        newContent={section.newContent}
                        onClick={() => {
                          handleSectionSelect(section.id);
                        }}
                      />
                    );
                  })}
                </div>
              </ScrollFadeRow>
            )}
          </div>

          {activeCongressSpaceId !== null ? (
            <RoomCongressResources
              roomId={roomId}
              visible={congressVisible}
              scope={{ kind: "chip", spaceId: activeCongressSpaceId }}
              now={now}
              layout={{ kind: "phone" }}
            />
          ) : resourcesQuery.state === "success" ? (
            // Drawn once the list is known: a block drawn before, then placed,
            // would mount twice and report its view twice.
            <RoomCongressResources
              roomId={roomId}
              visible={congressVisible}
              scope={{
                kind: "unplaced",
                placedSpaceIds: resourcesQuery.placedCongressSpaceIds,
              }}
              now={now}
              layout={{ kind: "phone" }}
            />
          ) : null}

          {isCongressFilter ? null : resourcesQuery.state === "error" ? (
            <p className="px-5 text-ui-body text-ds-slate-600">
              {resourcesQuery.message}
            </p>
          ) : resourcesQuery.state !== "success" ? (
            <RoomListWrapper key="sections-loading" className="px-5">
              {Array.from({ length: 10 }).map((_, index) => (
                <div
                  key={index}
                  className="animate-fade-in-up"
                  style={{
                    animationDelay: `${Math.round(60 * Math.sqrt(index))}ms`,
                    animationFillMode: "both",
                  }}
                >
                  <SkeletonPublicationCard showImage showDescription />
                </div>
              ))}
            </RoomListWrapper>
          ) : (
            <div key="content">
              {leadingBlocks.map((item) =>
                item.kind === "congress" ? (
                  <RoomCongressResources
                    key={resourceItemKey(item)}
                    roomId={roomId}
                    visible={true}
                    scope={{ kind: "placed", spaceId: item.spaceId }}
                    now={now}
                    layout={{ kind: "phone" }}
                  />
                ) : null
              )}
              <RoomEventDisplay
                roomId={roomId}
                roomTextColor={roomTextColor ?? null}
                pageType={EventDisplayDestination.RESOURCES}
              />
              <RoomListWrapper
                key={
                  selectedSection === null
                    ? "section-all"
                    : `section-${selectedSection.id}`
                }
                className="px-5"
              >
                {cardCount === 0 && (
                  <span className="no-results">
                    {t("No resources available yet for this room")}
                  </span>
                )}
                {listedItems.map((item, index) => {
                  const fadeClass = hadCachedSections.current
                    ? undefined
                    : "animate-fade-in-up";
                  const fadeStyle = hadCachedSections.current
                    ? undefined
                    : {
                        animationDelay: `${Math.round(60 * Math.sqrt(index))}ms`,
                        animationFillMode: "both" as const,
                      };
                  const itemKey = resourceItemKey(item);

                  if (item.kind === "congress") {
                    return (
                      <div key={itemKey} className="-mx-5">
                        <RoomCongressResources
                          roomId={roomId}
                          visible={true}
                          scope={{ kind: "placed", spaceId: item.spaceId }}
                          now={now}
                          layout={{ kind: "phone" }}
                        />
                      </div>
                    );
                  }

                  if (item.kind === "playlist") {
                    const { playlist } = item;
                    function openPlaylist() {
                      savePageScrollY();
                      gaEventSelectItemPlaylist({
                        playlist,
                        listName: ANALYTICS_LIST_NAME,
                      });
                      history.push({
                        pathname: publicPlaylistLink(playlist),
                        state: { playlist },
                      });
                    }
                    return (
                      <div
                        key={itemKey}
                        className={fadeClass}
                        style={fadeStyle}
                      >
                        <PlaylistCard
                          playlist={playlist}
                          variant="in-room"
                          primaryColor={room.primaryColor}
                          secondaryColor={room.secondaryColor}
                          onClick={openPlaylist}
                        />
                      </div>
                    );
                  }

                  if (item.kind === "article") {
                    const isNew =
                      isRecentArticle(item.article.meta.creationDate) &&
                      !hasReadArticle(item.article.id);
                    return (
                      <div
                        key={itemKey}
                        className={fadeClass}
                        style={fadeStyle}
                      >
                        <Badge
                          show={isNew}
                          label={t("New")}
                          position="tl"
                          className="z-10"
                          block
                        >
                          <ArticlePublicationCard
                            roomId={roomId}
                            article={item.article}
                            analyticsListName={ANALYTICS_LIST_NAME}
                            recommendation={null}
                            onClick={null}
                            className={null}
                            showImage={true}
                          />
                        </Badge>
                      </div>
                    );
                  }

                  if (item.kind === "podcast") {
                    return (
                      <div
                        key={itemKey}
                        className={fadeClass}
                        style={fadeStyle}
                      >
                        <PodcastCard
                          roomId={roomId}
                          podcast={item.podcast}
                          listIndex={index}
                          analyticsListName={ANALYTICS_LIST_NAME}
                          recommendation={null}
                          onClick={savePageScrollY}
                          className={null}
                        />
                      </div>
                    );
                  }

                  const { content } = item;
                  switch (content.content_format) {
                    case "Video":
                      return (
                        <div
                          key={itemKey}
                          className={fadeClass}
                          style={fadeStyle}
                        >
                          <VideoCard
                            roomId={roomId}
                            video={content}
                            analyticsListName={ANALYTICS_LIST_NAME}
                            recommendation={null}
                            onClick={savePageScrollY}
                            className={null}
                          />
                        </div>
                      );
                    case "Infographic":
                      return (
                        <div
                          key={itemKey}
                          className={fadeClass}
                          style={fadeStyle}
                        >
                          <InfographicCard
                            roomId={roomId}
                            infographic={content}
                            analyticsListName={ANALYTICS_LIST_NAME}
                            recommendation={null}
                            onClick={savePageScrollY}
                            className={null}
                          />
                        </div>
                      );
                    case "Audio": {
                      const audioThumb = thumbnailUrl(content);
                      return (
                        <div
                          key={itemKey}
                          className={fadeClass}
                          style={fadeStyle}
                        >
                          <ContentPublicationCard
                            contentType="podcast"
                            metaVariant="lemon"
                            title={content.title}
                            source={roomAudioSource(item)}
                            date={formatContentDate(
                              content.publication_date,
                              activeLang
                            )}
                            meta={t("Podcast")}
                            detail={formatAudioDuration(content.duration)}
                            imageUrl={audioThumb}
                            recommendation={null}
                            isBookmarked={false}
                            onBookmark={null}
                            onShare={null}
                            onClick={() => {
                              playAudioContent(content);
                            }}
                            className={undefined}
                          />
                        </div>
                      );
                    }
                    case "Nectar":
                      return null;
                    default:
                      throw new Error(content satisfies never);
                  }
                })}
              </RoomListWrapper>
            </div>
          )}
        </div>
      </ScrollableLayout>

      {renderShareSheet()}

      <RoomExplainerBottomSheet
        roomId={roomId}
        presentation="sheet"
        isOpen={isExplainerOpen}
        onClose={() => {
          setExplainerOpen(false);
        }}
      />
    </>
  );
}
